import { useContext, useState, useEffect } from "react";
import DownloadIcon from "@mui/icons-material/CreateNewFolder";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { useService } from "../applications-service";
import { ApplicationsContext } from "../applications-context";
import { StyledButton } from "../../../components/styled-button/styled-button";
import DateRangePicker from "../../../components/date-range-picker/date-range-picker";
import { AppConfig } from "../../../context/context";
import { saveAs } from "file-saver";

const ChooseDatesContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexWrap: "wrap",
  justifyContent: "flex-end",
  "& button.MuiButton-root": {
    "&.Mui-disabled": {
      opacity: "0.5 !important",
      backgroundColor: "var(--kyocera-bg-grey)",
      boxShadow:
        "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
      color: "var(--kyocera-text-grey)",
    },
    marginLeft: theme.spacing(2),
  },
}));

const ChooseDatesReportComponent = () => {
  const { appService } = useService();
  const {
    isAppListLoading,
    selectedApp,
    selectedCheckedApps,
    applications,
  } = useContext(ApplicationsContext);

  const { t } = useTranslation();
  const { config } = useContext(AppConfig);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedAppNames, setSelectedAppNames] = useState<string[]>([]);

  const today = new Date();

  const minSelectableDate = new Date();
  minSelectableDate.setDate(today.getDate() - 30);
  minSelectableDate.setHours(0, 0, 0, 0);

  useEffect(() => {
    if (selectedCheckedApps.length > 0 && applications?.devTenantApps) {
      const names = selectedCheckedApps.map((appId) => {
        const app = applications.devTenantApps.find(
          (app) => app.id === appId
        );
        return app ? app.name : "";
      });

      setSelectedAppNames(names);
    } else {
      setSelectedAppNames([]);
    }
  }, [selectedCheckedApps, applications]);

  const getFormattedRange = (startDate: Date, endDate: Date) => {
    const formatWithOffset = (date: Date, isEnd: boolean) => {
      const d = new Date(date);

      if (isEnd) {
        d.setHours(23, 59, 59, 999);
      } else {
        d.setHours(0, 0, 0, 0);
      }

      // 2. Build the string: YYYY-MM-DDTHH:mm:ss
      const pad = (n: number) => n.toString().padStart(2, "0");

      const t = `${d.getFullYear()}-${pad(
        d.getMonth() + 1
      )}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}:${pad(d.getSeconds())}`;

      const offsetMinutes = d.getTimezoneOffset();
      const absOffset = Math.abs(offsetMinutes);
      const offsetHours = pad(Math.floor(absOffset / 60));
      const offsetMins = pad(absOffset % 60);
      const sign = offsetMinutes > 0 ? "-" : "+";

      return `${t}${sign}${offsetHours}:${offsetMins}`;
    };

    return {
      startParam: formatWithOffset(startDate, false),
      endParam: formatWithOffset(endDate, true),
    };
  };

  const formatDateForFilename = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleGenerateReport = async () => {
    if (!selectedApp || !startDate || !endDate) {
      return;
    }

    try {
      setLoading(true);

      const { startParam, endParam } = getFormattedRange(
        startDate,
        endDate
      );

      const response = await appService.getUsageReport(
        config.tenantId,
        selectedAppNames[0],
        encodeURIComponent(startParam),
        encodeURIComponent(endParam)
      );

      if (response) {
        const blob = new Blob([response], {
          type: "text/csv",
        });

        const startFilename = formatDateForFilename(startDate);
        const endFilename = formatDateForFilename(endDate);

        const fileName = `Usage_${selectedAppNames[0]}_${startFilename}_${endFilename}.csv`;

        saveAs(blob, fileName);
      } else {
        console.error(
          "Failed to generate report: No data received."
        );
      }
    } catch (error) {
      console.error("Error downloading report:", error);
    } finally {
      setLoading(false);
    }
  };

  const isDownloadButtonDisabled =
    !startDate ||
    !endDate ||
    !selectedApp ||
    isAppListLoading ||
    loading;

  return (
    <ChooseDatesContainer>
      <DateRangePicker
        disabled={!selectedApp || isAppListLoading}
        placeHolderText={t("choose_dates")}
        minDate={minSelectableDate}
        maxDate={today}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
      />

      <StyledButton
        variant="contained"
        onClick={handleGenerateReport}
        className="d-flex align-items-center"
        disabled={isDownloadButtonDisabled}
        startIcon={<DownloadIcon />}
      >
        {t("download_report")}
      </StyledButton>
    </ChooseDatesContainer>
  );
};

export default ChooseDatesReportComponent;